import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { type AdornisMailer } from '@adornis/mails/server/mailer.js';
import { getPasswordResetLink } from '@adornis/users/server/password.js';
import { getValidationLink } from '@adornis/users/server/validation.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { projectConfig } from '../config.js';
import { accessTokenUsageInfoExternal, accessTokenUsageInfoInternal } from '../server/mailTemplates.js';

@Entity()
export class CampusMails extends AdornisEntity {
  public static override _class = 'CampusMails';
  public static mailer: AdornisMailer | undefined = undefined;
  public static doubleOptPath = '/api/activate';
  public static passwordChangePath = `/authentication/reset-password`;

  public static setMailer(mailer: AdornisMailer) {
    this.mailer = mailer;
  }

  public static async getDoubleOptLink(userId: string, ttu: number = 1) {
    return getValidationLink(this.doubleOptPath, { ttu, userID: userId, userClass: ChancenwerkUser._class });
  }

  public static async getPasswordChangeLink(userId: string, reroute: string, ttu: number = 1) {
    let resetLink = await getPasswordResetLink(this.passwordChangePath, {
      ttu,
      userID: userId,
      userClass: ChancenwerkUser._class,
    });

    let link = '';

    // Build redirect link
    switch (reroute) {
      case 'chancencampus':
        link = projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';
        break;
      case 'chancenraum':
        link = projectConfig.get('RAUM_URL');
        break;
      case 'chancenlogistik':
        link = projectConfig.get('LOGISTIK_URL');
        break;
      default:
        link = projectConfig.get('NETZ_URL');
        break;
    }

    resetLink = resetLink + `&reroute=${encodeURIComponent(link)}`;
    return resetLink;
  }

  @Query(type => Boolean)
  public static sendRegistrationMail(@Arg('userID', type => String) userID: string) {
    return async (gqlFields: BaseQLSelectionSet<any>) => {
      const templates = await import('../server/mailTemplates');
      if (!this.mailer)
        throw new Error(
          'Trying to send mails before mailer is set! You have to init the mailer with CampusMails.setMailer(...) first!',
        );

      const user = await ChancenwerkUser.getByID<ChancenwerkUser>(userID)({ _class: 1, _id: 1, email: 1 });
      if (!user) throw new Error('Invalid userID given');

      try {
        await this.mailer.sendMail(
          await Message.compose({
            subject: 'Registrierung beim ChancenCampus',
            html: templates.registrationConfirmationSelfService({
              confirmationUrl: await this.getDoubleOptLink(userID),
            }),
          }),
          new Recipient(user.email),
        );
      } catch (error) {
        logger.error('Couldnt send Registermail to: ' + user.email + ' with the user id: ' + user._id);
      }

      return true;
    };
  }

  @Query(type => Boolean)
  public static sendRegistrationMailExternal(
    @Arg('userID', type => String) userID: string,
    @Arg('kpfID', type => String) kpfID: string,
  ) {
    return async () => {
      const templates = await import('../server/mailTemplates');
      if (!this.mailer)
        throw new Error(
          'Trying to send mails before mailer is set! You have to init the mailer with CampusMails.setMailer(...) first!',
        );
      // This is a public endpoint. Usage of raw collections, because the calling user is not logged in.
      const userColl = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);
      const kpfColl = await getRawCollection<EntityData<KPF>>(KPF._collectionName);
      const partnerColl = await getRawCollection<EntityData<Partner>>(Partner._collectionName);

      const user = await userColl.findOne({ _id: userID });
      const kpf = await kpfColl.findOne({ _id: kpfID });
      const school = await partnerColl.findOne({ _id: kpf?.schoolID });

      if (!user) throw new Error('Invalid userID given');
      if (!kpf) throw new Error('Invalid kpfID given');
      if (!school) throw new Error('Given kpfID has no school. Please contact an admin');

      try {
        await this.mailer.sendMail(
          await Message.compose({
            subject: 'Accountanfrage einer Lehrkraft',
            html: templates.registrationInformationOfExternalTeacherForAdmin({
              teacherPrename: user.vorname ?? 'Nicht angegeben',
              teacherLastname: user.nachname ?? 'Nicht angegeben',
              teacherEmail: user.email,
              teacherSchool: school.name ?? 'Nicht angegeben',
              kpfLink: projectConfig.get('RAUM_URL') + 'edit/kpf/' + kpf._id,
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      } catch (error) {
        logger.error('Couldnt send Registermail to verwaltung');
        return false;
      }

      return true;
    };
  }

  @Query(type => Boolean)
  public static sendPasswordChangeMail(
    @Arg('userID', type => String) userID: string,
    @Arg('rerouteAfterSuccess', type => String) rerouteAfterSuccess: string,
  ) {
    return async (gqlFields: void) => {
      const templates = await import('../server/mailTemplates');
      if (!this.mailer)
        throw new Error(
          'Trying to send mails before mailer is set! You have to init the mailer with CampusMails.setMailer(...) first!',
        );

      const user = await (
        await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName)
      ).findOne({ _id: userID });

      if (!user) throw new Error('Invalid userID given');

      let mail = [user.email];

      // *
      // * If this is a student and the email is a dummy email, use the studentCreationMailOverride
      // *
      if (user?._class == 'Student' && user?.email.includes('dummy-')) {
        const relationshipType = await import('@chancenwerk/db/semantics/StudentParticipationRelationship.js');
        const kpf = await import('@chancenwerk/master-data-management/kpf/entities/KPF.js');

        const relationshipCol = await getRawCollection<{ objectID: string }>(
          relationshipType.StudentParticipationRelationship._collectionName,
        );
        const kpfCollection = await getRawCollection<{ studentCreationMailOverride: string[] }>(
          kpf.KPF._collectionName,
        );

        const relations = (await relationshipCol
          .find({
            subjectID: user._id,
          })
          .toArray()) as Maybe<{ objectID: string }[]>;

        const kpfs = (await kpfCollection.find({ _id: { $in: relations?.map(r => r.objectID!) } }).toArray()) as Maybe<
          { studentCreationMailOverride: string[] }[]
        >;

        const override = kpfs?.[0]?.studentCreationMailOverride;
        if (!override) {
          throw new Error('No studentCreationMailOverride set for this user');
        }
        mail = override;
      }

      try {
        await this.mailer.sendMail(
          await Message.compose({
            subject: 'Ändern deines Passworts beim ChancenCampus',
            html: templates.changePasswordMessage({
              confirmationUrl: await this.getPasswordChangeLink(userID, rerouteAfterSuccess),
            }),
          }),
          new Recipient(mail),
        );
      } catch (error) {
        logger.error(error);
        logger.error('Couldnt send Registermail to: ' + user.email + ' with the user id: ' + user._id);
      }

      return true;
    };
  }

  @Query(type => Boolean)
  public static sendAccessTokenUsageInfoExternal(
    @Arg('accessTokenUsageCurrent', type => Int) accessTokenUsageCurrent: number,
    @Arg('accessTokenUsageLimit', type => Int) accessTokenUsageLimit: number,
    @Arg('accessToken', type => String) accessToken: string,
    @Arg('percentage', type => String) percentage: string,
    @Arg('kpfShort', type => String) kpfShort: string,
    @Arg('ansprechPersonMail', type => String) ansprechPersonMail?: string | undefined,
  ) {
    return async (gqlFields: void) => {
      if (!this.mailer)
        throw new Error(
          'Trying to send mails before mailer is set! You have to init the mailer with CampusMails.setMailer(...) first!',
        );

      try {
        await this.mailer.sendMail(
          await Message.compose({
            subject: `Der Zugangscode für Ihre Schule hat ${percentage}% der Nutzung erreicht`,
            html: accessTokenUsageInfoExternal({
              accessToken,
              accessTokenUsageCurrent,
              accessTokenUsageLimit,
              percentage,
              kpfShort,
            }),
          }),
          [new Recipient('chancencampus@chancenwerk.org')],
          // ansprechPersonMail
          //   ? [new Recipient(ansprechPersonMail), new Recipient('chancencampus@chancenwerk.org')]
          //   : [new Recipient('chancencampus@chancenwerk.org')],
        );
      } catch (error) {
        logger.error('Couldnt send accessTokenUsageInfo to: ' + error);
      }

      return true;
    };
  }

  @Query(type => Boolean)
  public static sendAccessTokenUsageInfoInternal(
    @Arg('accessTokenUsageCurrent', type => Int) accessTokenUsageCurrent: number,
    @Arg('accessTokenUsageLimit', type => Int) accessTokenUsageLimit: number,
    @Arg('accessToken', type => String) accessToken: string,
    @Arg('kpfShort', type => String) kpfShort: string,
    @Arg('kpfId', type => String) kpfId: string,
    @Arg('percentage', type => String) percentage: string,
    @Arg('ansprechPersonMail', type => String) ansprechPersonMail?: string | undefined,
  ) {
    return async (gqlFields: void) => {
      if (!this.mailer)
        throw new Error(
          'Trying to send mails before mailer is set! You have to init the mailer with CampusMails.setMailer(...) first!',
        );

      try {
        await this.mailer.sendMail(
          await Message.compose({
            subject: `Zugangscode für ${kpfShort} hat 90% der Nutzung erreicht`,
            html: accessTokenUsageInfoInternal({
              accessToken,
              kpfShort,
              kpfId,
              accessTokenUsageCurrent,
              accessTokenUsageLimit,
              percentage,
            }),
          }),
          [new Recipient('chancencampus@chancenwerk.org')],
          // ansprechPersonMail
          //   ? [new Recipient(ansprechPersonMail), new Recipient('tobias.herkel@chancenwerk.org')]
          //   : [new Recipient('tobias.herkel@chancenwerk.org')],
        );
      } catch (error) {
        logger.error('Couldnt send accessTokenUsageInfo to: ' + error);
      }

      return true;
    };
  }
}
